import type { Phase } from './runner';

/**
 * The progress numbers Android keeps in `HomeViewModel.UiState`.
 *
 * `Phase` carries these counts **only while it is `checking`**, and that is the
 * whole problem this rule solves. Read straight off the phase, the bar would
 * snap back to 0% during every between-rounds wait and again while the batch is
 * submitting — three visible resets in a run that never went backwards. Android
 * does not have the problem because its UiState fields are independent of the
 * phase; this is that difference paid for in one place.
 *
 * It used to be paid for in a `useState` + `useEffect` latch inside the
 * dashboard, which meant a second render for every one of the L×R phase
 * dispatches, on the busiest phase of the run. It is a pure function of
 * (previous counts, new phase), so it belongs in the reducer that already sees
 * every one of those dispatches — and here, where the reset rule can be tested
 * without a DOM.
 */
export interface RunProgress {
  checked: number;
  total: number;
  alive: number;
  dead: number;
  round: number;
  totalRounds: number;
}

/**
 * What a cycle starts from, and what it returns to.
 *
 * Frozen because it is handed out as a shared value: one caller writing into it
 * would move the starting line for every cycle afterwards.
 */
export const NO_PROGRESS: RunProgress = Object.freeze({
  checked: 0,
  total: 0,
  alive: 0,
  dead: 0,
  round: 0,
  totalRounds: 0,
});

/**
 * Latch the counts a `checking` phase carries; clear them when a new cycle
 * begins; leave them alone otherwise.
 *
 * `fetching` is the reset, because that is the phase the runner emits at the
 * very start of a cycle (and the reducer dispatches on `cycle-start`). A new
 * cycle genuinely does start from nothing, and carrying the last run's counts
 * into it would show a full bar for a run that has not probed a single link yet.
 *
 * Every other phase — `between-rounds`, `submitting`, `done`, `failed`, `idle`,
 * `waiting-for-network` — holds. Those are the moments the old latch existed to
 * survive.
 */
export function runProgress(previous: RunProgress, phase: Phase): RunProgress {
  switch (phase.kind) {
    case 'checking':
      return {
        checked: phase.checked,
        total: phase.total,
        alive: phase.alive,
        dead: phase.dead,
        round: phase.round,
        totalRounds: phase.totalRounds,
      };
    case 'fetching':
      return NO_PROGRESS;
    default:
      // Copied rather than handed back, because the caller is the loop's reducer
      // and `previous` is its whole state object. Returning it would let a
      // spread put every unrelated field back too.
      return {
        checked: previous.checked,
        total: previous.total,
        alive: previous.alive,
        dead: previous.dead,
        round: previous.round,
        totalRounds: previous.totalRounds,
      };
  }
}
